import React, { Suspense } from 'react';
import './App.scss';
import Auth0Login from './pages/Auth0Login';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ApiProvider from './graphql/ApiProvider';
import Register from './pages/Register';
import packageJson from '../package.json';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import WebApp from './WebApp';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Inspection from './pages/inspection_page/Inspection';
import Auth0Landing from './pages/Auth0Landing';
import { NotFound404 } from './pages/NotFound404';
import Inspections from './pages/fleet/Inspections';
import { paths } from './constants/strings';
import ErrorBoundaryPage from './pages/ErrorBoundaryPage';
import WorkspaceRoutes from './WorkspaceRoutes';
import { useOnlineStatus } from './hooks/offline-hooks/offline-misc.js';
import { UserProvider } from './providers/UserProvider.jsx';
import ErrorAccountNotFound from './pages/ErrorAccountNotFound.jsx';
import CompanySignUp from './pages/CompanySignUp/CompanySignUp.jsx';
import CompanyRegistration from './pages/CompanyRegistration/CompanyRegistration.jsx';
import SignOut from './pages/SignOut.jsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import DashboardV2 from './pages/dashboard/desktop/DashboardV2.jsx';
import OfflineDashboard from './pages/dashboard/desktop/OfflineDashboard.jsx';
import AddObservationNoAuth from './pages/observations/AddObservationNoAuth.jsx';
import AddAssessmentNoAuth from './pages/inspection_page/noAuth/AddAssessmentNoAuth.jsx';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import {
  ActionItemList,
  Audits,
  CPAPage,
  CountPage,
  ObservationPage,
  Toolbox,
  ToolboxTalkGenerator,
  Announcements,
  Analytics,
  Timecards,
  AddTimecard,
  Expense,
  Expenses,
  WorkspaceDetailsPage,
  VehicleDetailsPage,
  VehiclesPage,
  SettingsTabSystem,
  Incidents,
  IncidentReport,
  FilesPage,
  CreateVehicle,
  CreateQuestions,
  Observations,
  Tasks,
  UserProfile,
  CheckInList,
  Certifications,
  UserList,
  ScorecardPage,
  PermissionsAdminPage,
} from './AppLazyRoutes.jsx';
import QualityModule from './pages/quality/QualityModule.jsx';
// import Report from './pages/analytics/count_analytics/custom_reports/CustomReport.jsx';
// import Reports from './pages/analytics/count_analytics/custom_reports/CustomReports.jsx';
import CustomReport from './pages/analytics/count_analytics/custom_reports/CustomReport.jsx';
import CustomReports from './pages/analytics/count_analytics/custom_reports/CustomReports.jsx';
import AdminTemplateTable from './pages/fleet/AdminTemplateTable.jsx';
import RiskRegistry from './pages/risk_registry/RiskRegistry.jsx';
import RiskPage from './pages/risk_registry/RiskPage.jsx';

Bugsnag.start({
  apiKey: process.env.REACT_APP_BUGSNAG_API_KEY,
  plugins: [new BugsnagPluginReact()],
  releaseStage: process.env.REACT_APP_RELEASE_STAGE,
  appVersion: packageJson.version,
  notifyReleaseStages: ['production', 'staging'],
  appType: 'client',
  onError: function (event) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    event.setUser(user.id, user.email, `${user.firstName} ${user.lastName}`);
  },
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

function App() {
  const online = useOnlineStatus();
  const { enableCompanySignUpCaptcha } = useFlags();

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryPage}>
      <ApiProvider>
        <BrowserRouter>
          <Suspense>
            <Routes>
              <Route path="/403" element={<ErrorAccountNotFound />} />
              <Route path="/login" element={<Auth0Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/signout" element={<SignOut />} />
              <Route
                path="/company-sign-up"
                element={
                  enableCompanySignUpCaptcha ? (
                    <CompanySignUp />
                  ) : (
                    <CompanyRegistration />
                  )
                }
              />
              <Route
                path="/addObservation"
                element={<AddObservationNoAuth />}
              />
              <Route path="/addAssessment" element={<AddAssessmentNoAuth />} />
              <Route
                path="/"
                element={
                  <UserProvider>
                    <WebApp />
                  </UserProvider>
                }
              >
                <Route path={paths.announcements} element={<Announcements />} />
                <Route path="/redirect" element={<Auth0Landing />} />

                <Route
                  path="/workspace/:workspaceId"
                  element={<WorkspaceRoutes />}
                >
                  <Route
                    path={paths.dashboard}
                    element={
                      online ? (
                        <DashboardV2 />
                      ) : (
                        <Suspense fallback={null}>
                          <OfflineDashboard />
                        </Suspense>
                      )
                    }
                  />
                  <Route
                    path={paths.actionItems}
                    element={<ActionItemList disabled={!online} />}
                  />
                  <Route path={paths.assessments} element={<Inspections />} />
                  <Route
                    path={paths.files}
                    element={<FilesPage disabled={!online} />}
                  />
                  <Route
                    path={paths.assets}
                    element={<VehiclesPage disabled={!online} />}
                  />
                  <Route path="asset/create" element={<CreateVehicle />} />
                  <Route path="asset/:id" element={<VehicleDetailsPage />} />
                  <Route path="assessment/:id" element={<Inspection />} />
                  <Route
                    path="incidentTemplate/:id"
                    element={<CreateQuestions />}
                  />
                  <Route path="quality" element={<QualityModule />} />
                  <Route
                    path={paths.announcements}
                    element={<Announcements />}
                  />
                  <Route path="templates/:id" element={<CreateQuestions />} />
                  <Route
                    path="files"
                    element={<FilesPage disabled={!online} />}
                  />
                  <Route
                    path="workspacesettings"
                    element={<WorkspaceDetailsPage />}
                  />
                </Route>
                <Route path="" element={<Navigate to="/redirect" replace />} />
                <Route path="incident/:id" element={<IncidentReport />} />
                <Route path={paths.incidents} element={<Incidents />} />
                <Route path={paths.observations} element={<Observations />} />
                <Route
                  path={`${paths.observation}/:id`}
                  element={<ObservationPage />}
                />
                <Route
                  path={`${paths.timecards}`}
                  element={<Timecards disabled={!online} />}
                />
                <Route path={`${paths.timecard}`} element={<AddTimecard />} />

                <Route
                  path={`${paths.expenses}`}
                  element={<Expenses disabled={!online} />}
                />
                <Route path={`${paths.expense}`} element={<Expense />} />

                <Route path={paths.tasks} element={<Tasks />} />

                <Route path="settings" element={<SettingsTabSystem />} />
                <Route path="users" element={<UserList />} />
                <Route path="permissions" element={<PermissionsAdminPage />} />
                <Route path={paths.analytics} element={<CountPage />} />
                <Route path="cpa/:id" element={<CPAPage />} />
                <Route path={paths.audits} element={<Audits />} />
                <Route path={paths.scorecard} element={<ScorecardPage />} />
                <Route path="user/:id" element={<UserProfile />} />
                <Route
                  path={`${paths.analytics}/addgraph`}
                  element={<Analytics />}
                />
                <Route path={`reports`} element={<CustomReports />} />
                <Route path={`templates`} element={<AdminTemplateTable />} />
                <Route path={`report/:id`} element={<CustomReport />} />
                <Route path={paths.toolbox} element={<Toolbox />} />
                <Route
                  path={`${paths.toolbox}/:id`}
                  element={<ToolboxTalkGenerator />}
                />
                <Route
                  path={paths.certifications}
                  element={<Certifications />}
                />

                <Route path={paths.checkIns} element={<CheckInList />} />
                <Route path="/registry" element={<RiskRegistry />} />
                <Route path="/registry/:id" element={<RiskPage />} />
              </Route>
              <Route path="*" element={<NotFound404 />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ApiProvider>
    </ErrorBoundary>
  );
}

export default App;
